import { useTranslation } from 'react-i18next';

/** A height in metres and its colour. */
type Scale = readonly (readonly [number, string])[];

/** Each way's own colour, as its line is drawn; a way by any other means as a drive. */
export const MODE_COLOURS = {
  flight: '#3b6ef5',
  train: '#16a34a',
  bus: '#e07b00',
  ferry: '#0e9bb5',
  car: '#db2777',
  foot: '#475569',
} as const;

/** A way's means that has a colour of its own. */
export type Means = keyof typeof MODE_COLOURS;

/** Whether a line's kind is a way's means, drawn by its height in its own colour. */
export const isMeans = (kind: string): kind is Means => kind in MODE_COLOURS;

/** How high a way's scale goes, in metres: a flight to its cruise, a way on the ground to the
 * high passes. */
const topOf = (kind: string) => (kind === 'flight' ? 12_000 : 3000);

/** How many shades a way's scale is drawn in: a stretch of one shade is one line, no stripes. */
const SHADES = 24;

/** A height said: a number, not missing. */
const said = (height: number | null | undefined): height is number =>
  typeof height === 'number';

/**
 * A way's stretches by its height, each as long as its shade holds: the ground's small rises and
 * falls one colour, not a stripe at each point.
 */
export function stretchesOf(
  kind: string,
  path: readonly [number, number][],
  heights: readonly (number | null)[],
): { height: number; coordinates: [number, number][] }[] {
  const step = topOf(kind) / SHADES;
  const stretches: { shade: number; coordinates: [number, number][] }[] = [];
  let open: { shade: number; coordinates: [number, number][] } | null = null;
  path.forEach((to, at) => {
    const [from, low, high] = [path[at - 1], heights[at - 1], heights[at]];
    if (!from || !said(low) || !said(high)) {
      open = null;
      return;
    }
    const shade = Math.round((low + high) / 2 / step);
    if (open?.shade === shade) {
      open.coordinates.push(to);
      return;
    }
    open = { shade, coordinates: [from, to] };
    stretches.push(open);
  });
  return stretches.map(({ shade, coordinates }) => ({ height: shade * step, coordinates }));
}

/** A colour mixed with another, `part` of the way to it: `#rrggbb`. */
function mixed(colour: string, toward: string, part: number): string {
  const channel = (hex: string, at: number) => Number.parseInt(hex.slice(at, at + 2), 16);
  return `#${[1, 3, 5]
    .map((at) =>
      Math.round(channel(colour, at) + (channel(toward, at) - channel(colour, at)) * part)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/**
 * A way's colours by its height, in its own colour: light near the ground, itself halfway up, a
 * dark shade of it at the top — on the dark map, its top only a little darker, to be seen.
 */
export function scaleOf(kind: Means, dark: boolean): Scale {
  const own = MODE_COLOURS[kind];
  const top = topOf(kind);
  return [
    [0, mixed(own, '#ffffff', dark ? 0.55 : 0.45)],
    [top / 2, own],
    [top, mixed(own, '#000000', dark ? 0.25 : 0.55)],
  ];
}

/** A scale's colours along its bar, from the ground to its top. */
function barOf(scale: Scale, top: number): string {
  return `linear-gradient(to right, ${scale
    .map(([height, colour]) => `${colour} ${String((height / top) * 100)}%`)
    .join(', ')})`;
}

/** The colours a way is drawn in by its height, in its own colour: light near the ground, dark
 * at the top of its scale — a flight's to its cruise, a way on the ground's to the passes. */
export function HeightScale({ kind, dark }: { kind: Means; dark: boolean }) {
  const { t } = useTranslation();
  const top = topOf(kind) / 1000;
  const step = top / 4 >= 1 ? 3 : 1;
  const name = t(`mode.${kind}`);
  return (
    <div
      className="map-heights-scale"
      role="img"
      aria-label={t('map.heightsOf', { name, top: String(top) })}
    >
      <span
        className="map-heights-bar"
        style={{ background: barOf(scaleOf(kind, dark), top * 1000) }}
      />
      <span className="map-heights-marks">
        {Array.from({ length: top / step + 1 }, (_, at) => at * step).map((km) => (
          <span key={km}>{km === 0 ? '0' : `${String(km)} km`}</span>
        ))}
      </span>
    </div>
  );
}
